import * as Sentry from '@sentry/vue';
import { Platform } from 'quasar';
import { Browser } from 'src/../src-capacitor/node_modules/@capacitor/browser';
import { LOCALSTORAGE } from 'src/composables/const';
import usePlatformHelpers, { DEVICE } from 'src/composables/platformHelpers';
import { useUserStore } from 'src/store/userStore';
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router';

export const ROUTES = {
  HOME: 'Home',
  ABOUT: 'About',
  SIGN_UP: 'SignUp',
  TERMS: 'Terms',
  PRIVACY: 'Privacy',
  CONGRATULATIONS: 'Congratulations',
  UNINSTALL: 'Uninstall',
  DONATION: 'Donation',
  DELETE_ACCOUNT: 'DeleteAccount',
  PRELANDER: 'Prelander',
  PRELANDER3: 'Prelander3',
  ONBOARDING: 'Onboarding',
  NOT_FOUND: 'NotFound',
  TOKEN_AUTH: 'TokenAuth',
  SEARCH: 'Search',
  FEEDBACK: 'Feedback',
  HOW_IT_WORKS: 'HowItWorks',
};

export function getRoutePath(routeName: string) {
  return routes.find(
    (route) => (<string>route.name)?.length && route.name === routeName
  )?.path;
}

export function capacitorGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  if (Platform.is.capacitor) {
    return next('/');
  }
  next();
}

export async function searchPageGuard(to: RouteLocationNormalized) {
  if (Platform.is.capacitor) {
    await Browser.open({
      url: `https://yssads.ddc.com/yhs.php?c=24054&surl=https://cheerie.org&kw=${encodeURIComponent(
        to.query.q as string
      )}`,
    });
  } else {
    window.location.href = `https://yssads.ddc.com/yhs.php?c=24054&surl=https://cheerie.org&kw=${
      to.query.q as string
    }`;
  }
}

export function capacitorOnlyGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  if (!Platform.is.capacitor) {
    return next('/');
  }
  next();
}

const routes: RouteRecordRaw[] = [
  {
    name: ROUTES.HOME,
    path: '/',
    component: () => import('pages/HomePage.vue'),
  },
  {
    name: ROUTES.SEARCH,
    path: '/search',
    beforeEnter: searchPageGuard,
    component: () => undefined,
  },
  {
    name: ROUTES.ABOUT,
    path: '/about',
    component: () => import('pages/AboutPage.vue'),
  },
  {
    name: ROUTES.TERMS,
    path: '/terms',
    component: () => import('pages/TermsPage.vue'),
  },
  {
    name: ROUTES.PRIVACY,
    path: '/privacy',
    component: () => import('pages/PrivacyPolicy.vue'),
  },
  {
    name: ROUTES.CONGRATULATIONS,
    path: '/congratulations',
    component: () => import('pages/CongratulationsPage.vue'),
    beforeEnter: capacitorGuard,
  },
  {
    name: ROUTES.SIGN_UP,
    path: '/signup',
    component: () => import('pages/SignUpPageMobile.vue'),
    beforeEnter: capacitorOnlyGuard,
  },
  {
    name: ROUTES.UNINSTALL,
    path: '/uninstall',
    component: () => import('pages/UninstallPage.vue'),
    beforeEnter: capacitorGuard,
  },
  {
    name: ROUTES.DELETE_ACCOUNT,
    path: '/delete-account',
    component: () => import('pages/DeleteAccountPage.vue'),
    beforeEnter: (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      const user = useUserStore();
      if (!user.isLoggedIn) {
        return next('/');
      }

      return next();
    },
  },
  {
    name: ROUTES.DONATION,
    path: '/donation',
    component: () => import('pages/DonationPage.vue'),
  },
  {
    name: ROUTES.PRELANDER,
    path: '/pl1',
    component: () => import('pages/Prelander.vue'),
    beforeEnter: capacitorGuard,
  },
  {
    name: ROUTES.PRELANDER3,
    path: '/pl3',
    component: () => import('pages/Prelander3.vue'),
    beforeEnter: capacitorGuard,
  },
  {
    name: ROUTES.ONBOARDING,
    path: '/onboarding',
    component: () => import('pages/OnboardingPage.vue'),
    beforeEnter: (to, from, next) => {
      if (Platform.is.capacitor) {
        const didOpen = localStorage.getItem(LOCALSTORAGE.ONBOARDING);
        if (didOpen) next('/');
        else next();
      } else {
        next('/');
      }
    },
  },
  {
    name: ROUTES.NOT_FOUND,
    path: '/404',
    component: () => import('pages/NotFoundPage.vue'),
  },
  {
    name: ROUTES.TOKEN_AUTH,
    path: '/auth/verify',
    props: true,
    component: () => undefined,
    beforeEnter: async (to, _, next) => {
      const user = useUserStore();
      const { whichDevice } = usePlatformHelpers();
      let token = user.token;
      if (whichDevice() === DEVICE.DESKTOP) {
        token = to.query.token as string;
        localStorage.setItem(LOCALSTORAGE.TOKEN, token);
        user.setToken(token);
      }

      try {
        await user.getMe();
      } catch (error) {
        Sentry.captureException(error);
        localStorage.removeItem(LOCALSTORAGE.TOKEN);
        user.setToken(null);
      } finally {
        next('/');
      }
    },
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/NotFoundPage.vue'),
  },
];

export default routes;
